import { Search } from 'iconoir-react'
import { Input } from '@/components/ui/input'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import styles from './simple-search-bar.module.scss'
import { cn } from '@/lib/utils'
import debounce from 'lodash/debounce'
import { useForm } from 'react-hook-form'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

type SimpleSearchBarProps = React.HTMLAttributes<HTMLDivElement> & {
  placeholder?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  onSearch?: (value: string) => void
  debounce?: number
  resetOnSubmit?: boolean
  icon?: React.ReactNode
}

const formSchema = z.object({
  search: z.string(),
})

type FormValues = z.infer<typeof formSchema>

// TODO: Transform TailwindCSS classes to css modules
const SimpleSearchBar = forwardRef<
  {
    clear: () => void
  },
  SimpleSearchBarProps
>(function SimpleSearchBar(
  {
    className,
    placeholder,
    defaultValue,
    onValueChange,
    onSearch,
    debounce: debounceTime = 300,
    resetOnSubmit = true,
    icon,
  },
  ref
) {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      search: defaultValue ?? '',
    },
  })

  const watchSearch = form.watch('search')

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value

      if (onValueChange) {
        onValueChange(value)
      }
    },
    [onValueChange]
  )

  const debouncedChange = useMemo(() => {
    return debounce(handleChange, debounceTime)
  }, [debounceTime, handleChange])

  useEffect(() => {
    const value = (watchSearch ?? '').trim()

    if (!!value) {
      debouncedChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>)
    } else {
      debouncedChange.cancel()
      handleChange({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>)
    }
  }, [debouncedChange, handleChange, watchSearch])

  const onSubmit = (data: FormValues) => {
    if (onSearch) {
      onSearch(data.search)

      // Reset the form
      if (resetOnSubmit) {
        form.resetField('search', {
          defaultValue: '',
        })
      }
    }
  }

  useEffect(() => {
    return () => {
      debouncedChange.cancel()
    }
  }, [debouncedChange])

  const internalInputRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(
    ref,
    () => ({
      clear: () => {
        form.resetField('search', {
          defaultValue: '',
        })
      },
    }),
    [form]
  )

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div
          className={cn(
            'tw-relative tw-flex tw-flex-row tw-items-center tw-justify-between tw-overflow-hidden tw-rounded-[4px] tw-bg-[var(--Primary-400)] tw-p-1',
            className
          )}
        >
          {!!icon ? icon : <Search width="32" color="#F36C17" strokeWidth="3" />}
          <FormField
            control={form.control}
            name="search"
            render={({ field }) => (
              <FormItem className="tw-w-full">
                <FormControl>
                  <Input
                    {...field}
                    ref={internalInputRef}
                    type="search"
                    placeholder={placeholder ?? 'Search...'}
                    variant="borderless"
                    className={cn(styles.input, '!tw-h-auto tw-w-full')}
                    autoComplete={'off'}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  )
})

export { SimpleSearchBar }
export type { SimpleSearchBarProps }
